import { fetchLabelRTO_CSV, fetchLabelRTO_PDF, fetchRTOProof, fetchRtoOrders } from '@/api';
import { PageLayout } from '@/components';
import { TicketEntityIDFieldEnum } from '@/constants';
import { useOpenEntityTicketModal, useOrderListPagination } from '@/hooks';
import { useFetchSingleRTOById } from '@/hooksApi';
import {
  downloadFile,
  isValidLatLng,
  triggerDownload,
  formatWithThousandSeparator,
  formatDateByDayjs,
  formatPhoneDisplay,
} from '@/utils';
import { Dropdown, notification, message } from 'antd';
import { Button } from '@/components/ui/button';
import { useCallback, useState } from 'react';
import {
  Copy,
  Phone,
  MessageCircle,
  Info,
  Download,
  FileText,
  MoreHorizontal,
  MapPin,
  ArrowLeft,
  Ticket,
  ChevronDown,
} from 'lucide-react';
import MapComponent from '@/components/Map/MapComponent';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { getStatusColors } from '@/pages/order-details/components/StatusColors';
import { RtoNoteCard } from '@/components/Order/RtoNote/RtoNoteCard';
import { RtoOrderBoxes } from './RtoOrderBoxes';

const RTOSingle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermission();
  const [isDownloadingProof, setIsDownloadingProof] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'orders'>('details');

  const { data: dataSingleRTO, isLoading: loadingSingleRTO } = useFetchSingleRTOById(
    id as string,
    600000,
  );
  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.PICKUP);

  const response = dataSingleRTO?.object;
  const statusSlug = response?.status?.slug || response?.status?.title || 'unknown';
  const isDelivered = ['delivered', 'rto-delivered', 'rto-partially-delivered'].includes(
    statusSlug,
  );

  const getStatusActiveRTO = (slug: string): number => {
    switch (slug) {
      case 'rto-scheduled':
      case 'scheduled':
        return 0;
      case 'rto-in-transit':
      case 'rto-drop-off-scanned':
      case 'out-for-delivery':
      case 'returning-origin':
        return 1;
      case 'rto-delivered':
      case 'delivered':
      case 'rto-partially-delivered':
      case 'returned-warehouse':
        return 2;
      default:
        return -1;
    }
  };

  const currentStep = getStatusActiveRTO(statusSlug);

  const steps = [
    { label: String(t('scheduled')), statusLabel: '' },
    { label: String(t('returning', { defaultValue: 'Returning' })), statusLabel: '' },
    { label: String(t('returned', { defaultValue: 'Returned' })), statusLabel: '' },
  ];

  steps.forEach((step, i) => {
    if (i < currentStep) step.statusLabel = String(t('completed', { defaultValue: 'Completed' }));
    else if (i === currentStep)
      step.statusLabel = String(t('in_progress_step', { defaultValue: 'In progress' }));
    else step.statusLabel = String(t('pending', { defaultValue: 'Pending' }));
  });

  const onDownloadProof = async () => {
    setIsDownloadingProof(true);
    try {
      const blob = await fetchRTOProof(response?.uid ?? (id as string));
      const url = window.URL.createObjectURL(blob);
      triggerDownload(url, `RTO_Proof_${response?.platform_code || id}.pdf`);
      window.URL.revokeObjectURL(url);
      notification.success({
        message: t('download_ready'),
        description: t('your_file_is_downloading'),
        duration: 5,
      });
    } catch {
      notification.error({ message: t('operation_failed_please_try_again'), duration: 5 });
    }
    setIsDownloadingProof(false);
  };

  const handleCopyId = () => {
    if (response?.platform_code) {
      navigator.clipboard.writeText(response.platform_code);
      message.success(t('copied'));
    }
  };

  const kebabItems = [
    {
      key: 'pdf',
      label: (
        <span className='flex items-center gap-2 text-[13px] text-[rgb(57,57,60)]'>
          <FileText size={14} />
          {t('download_rto_pdf', { defaultValue: 'Download PDF' })}
        </span>
      ),
      onClick: async () => {
        await downloadFile(response?.uid ?? '', fetchLabelRTO_PDF, `RTO_${response?.uid}.pdf`);
      },
    },
    {
      key: 'csv',
      label: (
        <span className='flex items-center gap-2 text-[13px] text-[rgb(57,57,60)]'>
          <Download size={14} />
          {t('download_rto_csv', { defaultValue: 'Download CSV' })}
        </span>
      ),
      onClick: async () => {
        await downloadFile(response?.uid ?? '', fetchLabelRTO_CSV, `RTO_${response?.uid}.csv`);
      },
    },
    ...(isDelivered
      ? [
          {
            key: 'proof',
            label: (
              <span className='flex items-center gap-2 text-[13px] text-[rgb(57,57,60)]'>
                <Download size={14} />
                {t('download_rto_proof', { defaultValue: 'Download Proof' })}
              </span>
            ),
            onClick: onDownloadProof,
          },
        ]
      : []),
  ];

  const getStatusColor = () => {
    if (isDelivered) return { bg: 'rgba(34,197,94,0.12)', text: 'rgb(22,163,74)' };
    if (
      statusSlug.includes('transit') ||
      statusSlug.includes('delivery') ||
      statusSlug.includes('returning')
    )
      return { bg: 'rgba(59,130,246,0.12)', text: 'rgb(37,99,235)' };
    return { bg: 'rgba(168,85,247,0.12)', text: 'rgb(147,51,234)' };
  };

  if (loadingSingleRTO) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='w-8 h-8 border-2 border-[rgb(237,224,74)] border-t-transparent rounded-full animate-spin' />
      </div>
    );
  }

  if (!response) {
    return (
      <PageLayout>
        <div className='flex flex-col items-center justify-center min-h-[400px] gap-4'>
          <p className='text-[15px] text-[rgb(115,123,140)]'>
            {t('return_not_found', { defaultValue: 'Return not found' })}
          </p>
          <button
            type='button'
            onClick={() => navigate('/pickups')}
            className='flex items-center gap-2 px-4 py-2 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[13px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          >
            {t('back_to_pickups', { defaultValue: 'Back to Shipments' })}
          </button>
        </div>
      </PageLayout>
    );
  }

  const statusColor = getStatusColor();

  return (
    <PageLayout>
      {/* ─── Sub-header: ID + date + status ─── */}
      <div className='flex items-start justify-between mb-5'>
        <div>
          <div className='flex items-center gap-2'>
            <span className='text-xl font-bold tracking-tight'>
              {response?.platform_code ?? '—'}
            </span>
            <button
              type='button'
              onClick={handleCopyId}
              className='p-1 rounded-md hover:bg-sd-muted transition-colors cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground'
            >
              <Copy className='h-4 w-4' strokeWidth={2} />
            </button>
          </div>
          <p className='text-[12px] text-sd-muted-foreground mt-1 m-0'>
            {response?.created_at ? formatDateByDayjs(response.created_at, 'D MMMM YYYY') : ''}
          </p>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <span
            className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap'
            style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
          >
            {String(
              t(`merchant_rto_status.${statusSlug}`, {
                defaultValue: response?.status?.title ?? statusSlug,
              }),
            )}
          </span>
          {hasPermission(PermissionName.CREATE_TICKET) && (
            <button
              onClick={() => openEntityTicketModal(response?.platform_code)}
              className='flex items-center gap-1.5 h-8 px-3 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[11px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              <Ticket size={13} />
              {String(t('ticket'))}
            </button>
          )}
          <Dropdown menu={{ items: kebabItems }} trigger={['click']} placement='bottomRight'>
            <button
              type='button'
              className='flex items-center justify-center w-8 h-8 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              <MoreHorizontal size={16} />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div className='flex w-full border-b border-[rgb(232,230,227)] mb-6 mt-4'>
        <button
          onClick={() => setActiveTab('details')}
          className={`px-5 pb-2.5 pt-1 text-[13px] font-semibold border-0 border-b-2 border-solid -mb-px transition-colors cursor-pointer bg-transparent ${activeTab === 'details' ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]' : 'border-b-transparent text-[rgb(115,123,140)]'}`}
        >
          {String(t('details', { defaultValue: 'Details' }))}
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-5 pb-2.5 pt-1 text-[13px] font-semibold border-0 border-b-2 border-solid -mb-px transition-colors cursor-pointer bg-transparent ${activeTab === 'orders' ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]' : 'border-b-transparent text-[rgb(115,123,140)]'}`}
        >
          {String(t('orders'))}
        </button>
      </div>

      {/* ─── Details Tab ─── */}
      {activeTab === 'details' && (
        <div className='grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start'>
          <div className='flex flex-col gap-5'>
            {/* TRACKING TIMELINE */}
            <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-6'>
              <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)] block mb-6'>
                {String(t('tracking_timeline', { defaultValue: 'Tracking Timeline' }))}
              </span>
              <div className='flex items-start justify-between'>
                {steps.map((step, index) => {
                  const isCompleted = index < currentStep;
                  const isCurrent = index === currentStep;
                  const isLast = index === steps.length - 1;
                  return (
                    <div key={index} className='flex items-start flex-1'>
                      <div className='flex flex-col items-center'>
                        <div
                          className='w-6 h-6 rounded-full border-2 flex items-center justify-center'
                          style={{
                            borderColor:
                              isCompleted || isCurrent ? 'rgb(237,224,74)' : 'hsl(40,10%,80%)',
                            backgroundColor: isCompleted
                              ? 'rgb(237,224,74)'
                              : isCurrent
                                ? 'rgba(237,224,74,0.15)'
                                : 'hsl(40,10%,96%)',
                          }}
                        >
                          {isCompleted && (
                            <svg
                              width='10'
                              height='10'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='rgb(57,57,60)'
                              strokeWidth='3'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <polyline points='20 6 9 17 4 12' />
                            </svg>
                          )}
                          {isCurrent && (
                            <div className='w-2.5 h-2.5 rounded-full bg-[rgb(237,224,74)]' />
                          )}
                          {!isCompleted && !isCurrent && (
                            <div className='w-1.5 h-1.5 rounded-full bg-[hsl(40,10%,75%)]' />
                          )}
                        </div>
                        <span className='text-[12px] font-medium text-[rgb(57,57,60)] mt-2 text-center'>
                          {step.label}
                        </span>
                        <span className='text-[10px] text-[rgb(115,123,140)] mt-0.5 text-center'>
                          {step.statusLabel}
                        </span>
                      </div>
                      {!isLast && (
                        <div
                          className='flex-1 h-0.5 mt-2.5 mx-3'
                          style={{
                            backgroundColor: isCompleted ? 'rgb(237,224,74)' : 'rgb(232,230,227)',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* DRIVER */}
            <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
              <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)] block mb-4'>
                {String(t('driver'))}
              </span>
              {response?.driver ? (
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-3'>
                    <div
                      className='w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white text-[13px] font-semibold'
                      style={{
                        backgroundColor: `hsl(${((response.driver.first_name || '').charCodeAt(0) * 37) % 360}, 55%, 45%)`,
                      }}
                    >
                      {`${(response.driver.first_name ?? '')[0] || ''}${(response.driver.last_name ?? '')[0] || ''}`.toUpperCase()}
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-[14px] font-semibold text-[rgb(57,57,60)]'>
                        {`${response.driver.first_name || ''} ${response.driver.last_name || ''}`.trim()}
                      </span>
                      <span className='text-[12px] text-[rgb(115,123,140)]' dir='ltr'>
                        {formatPhoneDisplay(response.driver.phone)}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    {response.driver.phone && (
                      <>
                        <a
                          href={`tel:${response.driver.phone}`}
                          className='w-9 h-9 rounded-full border border-solid border-[rgb(232,230,227)] bg-white flex items-center justify-center hover:bg-[rgb(245,245,243)] transition-colors'
                        >
                          <Phone size={16} className='text-[rgb(57,57,60)]' />
                        </a>
                        <a
                          href={`https://wa.me/${response.driver.phone}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='w-9 h-9 rounded-full border border-solid border-[rgb(232,230,227)] bg-white flex items-center justify-center hover:bg-[rgb(245,245,243)] transition-colors'
                        >
                          <MessageCircle size={16} className='text-[rgb(57,57,60)]' />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className='flex items-center gap-3 py-2'>
                  <div className='w-10 h-10 rounded-full bg-[rgb(245,245,243)] flex items-center justify-center'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='rgb(115,123,140)'
                      strokeWidth='2'
                    >
                      <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
                      <circle cx='12' cy='7' r='4' />
                    </svg>
                  </div>
                  <span className='text-sm text-[rgb(115,123,140)]'>
                    {String(t('order_assign'))}
                  </span>
                </div>
              )}
            </section>
          </div>

          {/* RTO Summary + Map — right column */}
          <div className='flex flex-col gap-5 pb-8'>
            {/* SUMMARY — above map */}
            <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
              <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)] block mb-4'>
                {String(t('summary', { defaultValue: 'Summary' }))}
              </span>
              <div className='divide-y divide-[rgb(232,230,227)]'>
                {response?.orders_count != null && (
                  <div className='flex items-center justify-between py-2.5 first:pt-0 last:pb-0'>
                    <span className='text-[13px] text-[rgb(115,123,140)]'>
                      {String(t('orders_count', { defaultValue: 'Orders' }))}
                    </span>
                    <span className='text-[13px] font-semibold text-[rgb(57,57,60)]'>
                      {response.orders_count}
                    </span>
                  </div>
                )}
                {response?.order_value != null && (
                  <div className='flex items-center justify-between py-2.5 first:pt-0 last:pb-0'>
                    <span className='text-[13px] text-[rgb(115,123,140)]'>
                      {String(t('order_value', { defaultValue: 'Order Value' }))}
                    </span>
                    <span className='text-[13px] font-semibold text-[rgb(57,57,60)]'>
                      {formatWithThousandSeparator(response.order_value)} {String(t('iqd'))}
                    </span>
                  </div>
                )}
                {/* Shipping fee hidden for RTO — returns don't charge shipping */}
                {response?.total_price != null && (
                  <div className='flex items-center justify-between py-2.5 first:pt-0 last:pb-0'>
                    <span className='text-[13px] font-semibold text-[rgb(57,57,60)]'>
                      {String(t('total', { defaultValue: 'Total' }))}
                    </span>
                    <span className='text-[14px] font-bold text-[rgb(57,57,60)]'>
                      {formatWithThousandSeparator(response.total_price)} {String(t('iqd'))}
                    </span>
                  </div>
                )}
              </div>
            </section>
            {/* MAP — below summary */}
            {response?.task &&
              isValidLatLng(Number(response.task.lat), Number(response.task.lng)) && (
                <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] overflow-hidden'>
                  <div className='p-4 pb-0'>
                    <div className='flex items-center gap-2 mb-2'>
                      <MapPin size={14} className='text-[rgb(115,123,140)]' />
                      <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
                        {String(t('location', { defaultValue: 'Location' }))}
                      </span>
                    </div>
                  </div>
                  <div style={{ height: 200 }}>
                    <MapComponent
                      targetLocation={{
                        latitude: Number(response.task.lat),
                        longitude: Number(response.task.lng),
                      }}
                      driverLocation={(() => {
                        const taskData = response.task as any;
                        const driverPos = taskData?.task_list?.driver?.last_position;
                        return driverPos &&
                          isValidLatLng(Number(driverPos.lat), Number(driverPos.lng))
                          ? { latitude: Number(driverPos.lat), longitude: Number(driverPos.lng) }
                          : undefined;
                      })()}
                    />
                  </div>
                </section>
              )}
          </div>
        </div>
      )}

      {/* ─── Orders Tab ─── */}
      {activeTab === 'orders' && <RtoOrdersList rtoUid={id as string} />}
    </PageLayout>
  );
};

/* ─── RTO Orders List sub-component ─── */
const RTO_ORDERS_PAGE_SIZE = 25;

function RtoOrdersList({ rtoUid }: { rtoUid: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Server-side load-more (25/batch) — same as pickup, the RTO order list was
  // previously rendered in full.
  const fetcher = useCallback(
    async (page: number) => {
      const res = await fetchRtoOrders(rtoUid, page, RTO_ORDERS_PAGE_SIZE);
      const obj = res?.object ?? {};
      const items = Array.isArray(obj) ? obj : (obj.items ?? []);
      return {
        items,
        page: obj.page ?? page,
        total: obj.total ?? items.length,
        pages: obj.pages ?? 1,
      };
    },
    [rtoUid],
  );

  const {
    items: orderList,
    total,
    isLoading,
    loadingMore,
    hasMore,
    loadMore,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useOrderListPagination<any>(fetcher);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-10'>
        <div className='w-6 h-6 border-2 border-[rgb(237,224,74)] border-t-transparent rounded-full animate-spin' />
      </div>
    );
  }

  if (orderList.length === 0) {
    return (
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-8 text-center'>
        <span className='text-sm text-[rgb(115,123,140)]'>
          {String(t('no_orders_in_return', { defaultValue: 'No orders in this return' }))}
        </span>
      </div>
    );
  }

  const getStatusColor = (slug: string) => getStatusColors(slug);

  return (
    <div>
      <div className='flex items-center justify-between mb-3'>
        <span className='text-[12px] text-[rgb(115,123,140)]'>
          {total} {String(t('orders'))}
        </span>
      </div>
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] overflow-hidden'>
        <div className='grid grid-cols-[1fr_1fr_1fr_120px] gap-4 px-5 py-3 border-b border-[rgb(232,230,227)]'>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('order'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('drop_off_location'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('driver'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('status'))}
          </span>
        </div>
        {orderList.map((order: any) => {
          const statusSlug = order.status?.slug || '';
          const statusColor = getStatusColor(statusSlug);
          const driverName = order.driver?.first_name
            ? `${order.driver.first_name} ${order.driver.last_name || ''}`.trim()
            : '';
          const driverInitials = driverName
            ? driverName
                .split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2)
            : '';
          return (
            <div
              key={order.uid || order.id}
              className='border-0 border-b border-solid border-[rgb(232,230,227)]'
            >
              <button
                type='button'
                onClick={() => order.uid && navigate(`/orders/${order.uid}`)}
                className='grid grid-cols-[1fr_1fr_1fr_120px] gap-4 px-5 py-3.5 text-start cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors w-full bg-transparent border-0'
              >
                <div className='flex flex-col gap-0.5'>
                  <div className='flex items-center gap-1.5'>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: 'rgb(57,57,60)',
                      }}
                    >
                      {order.platform_code ?? '—'}
                    </span>
                    <Copy
                      size={12}
                      className='text-gray-400 hover:text-gray-600 shrink-0'
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(order.platform_code ?? '');
                        message.success(String(t('copied')));
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>
                    {formatDateByDayjs(order.created_at, 'DD MMM YYYY')}
                  </span>
                  {order.rto_note ? <RtoNoteCard note={order.rto_note} compact /> : null}
                </div>
                <div className='flex items-center gap-1.5'>
                  <MapPin size={14} className='text-[rgb(115,123,140)] shrink-0' />
                  <span className='text-[12px] text-[rgb(115,123,140)] truncate'>
                    {order.address_text || order.region?.area_name || '—'}
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  {driverName ? (
                    <>
                      <div
                        className='w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0'
                        style={{
                          backgroundColor: `hsl(${(driverName.charCodeAt(0) * 37) % 360}, 55%, 45%)`,
                        }}
                      >
                        {driverInitials}
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-[12px] font-medium text-[rgb(57,57,60)]'>
                          {driverName}
                        </span>
                        {order.driver?.phone && (
                          <span className='text-[11px] text-[rgb(115,123,140)]' dir='ltr'>
                            {formatPhoneDisplay(order.driver.phone)}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className='text-[12px] text-[rgb(115,123,140)]'>—</span>
                  )}
                </div>
                <div className='flex items-center'>
                  <span
                    className='inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap'
                    style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                  >
                    {order.status?.merchant_title ||
                      String(t(`merchant_title_order_status.${statusSlug}`))}
                  </span>
                </div>
              </button>
              {/* BOXY-556 — per-box return line, outside the row button. */}
              <RtoOrderBoxes boxes={order.boxes} statusSlug={statusSlug} />
            </div>
          );
        })}
      </div>

      {hasMore ? (
        <div className='mt-4 flex justify-center'>
          <Button
            variant='default'
            onClick={loadMore}
            disabled={loadingMore}
            className='gap-2 px-5 font-semibold shadow-sm'
          >
            {loadingMore ? (
              <span className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
            ) : (
              <ChevronDown className='h-4 w-4' />
            )}
            {String(t('load_more', { defaultValue: 'Load more' }))}
            <span className='text-xs font-normal opacity-70'>
              {orderList.length}/{total}
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default RTOSingle;
